import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Hash, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../../components/Navbar";
import affiliateService from "../../requests/admin/AffiliateService";
import StepperNavigation from "../../components/PromoterApproval/StepperNavigation";
import BasicValidationChecks from "../../components/PromoterApproval/BasicValidationChecks";
import ChannelQualityAssessment from "../../components/PromoterApproval/ChannelQualityAssessment";
import RiskComplianceReview from "../../components/PromoterApproval/RiskComplianceReview";
import ContactInfoSection from "../../components/PromoterApproval/ContactInfoSection";
import PromoterProfileDetails from "../../components/PromoterApproval/PromoterProfileDetails";
import ActionButtons from "../../components/PromoterApproval/ActionButtons";
import ActivationRequirements from "../../components/PromoterApproval/ActivationRequirements";
import PublisherScoringModel from "../../components/PromoterApproval/PublisherScoringModel";
import PublisherLevelAssignment from "../../components/PromoterApproval/PublisherLevelAssignment";
import { calculateProfileCompleteness, countPromotionChannels } from "../../utils/profileCompleteness";

const PromoterApproval = () => {
  const { affiliateCode } = useParams();
  const navigate = useNavigate();
  const [promoter, setPromoter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [channelRatings, setChannelRatings] = useState({});
  const [riskChecks, setRiskChecks] = useState({});
  const [currentStep, setCurrentStep] = useState("profile");
  const [scoringData, setScoringData] = useState(null);
  const [levelAssignment, setLevelAssignment] = useState({
    level: 1,
    level_name: "Basic Publisher",
  });

  const loadPromoterDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await affiliateService.showAffiliateDetails(affiliateCode);
      if (response.data?.status_code === "000") {
        const promoterData = response.data.data;
        setPromoter(promoterData);
        
        if (promoterData.risk_checks && Array.isArray(promoterData.risk_checks) && promoterData.risk_checks.length > 0) {
          const riskChecksObj = {};
          promoterData.risk_checks.forEach(check => {
            riskChecksObj[check.check_id] = check.passed;
          });
          setRiskChecks(riskChecksObj);
        } else {
          setRiskChecks({
            no_spam: null,
            no_fake_followers: null,
            no_misleading_content: null,
            no_previous_abuse: null,
          });
        }

        if (promoterData.channel_ratings && Array.isArray(promoterData.channel_ratings) && promoterData.channel_ratings.length > 0) {
          const channelRatingsObj = {};
          promoterData.channel_ratings.forEach(rating => {
            const channelKey = `${rating.platform}_${rating.channel_index}`;
            const ratingsObj = {};
            rating.ratings.forEach(r => {
              ratingsObj[r.criteria] = r.rating;
            });
            channelRatingsObj[channelKey] = ratingsObj;
          });
          setChannelRatings(channelRatingsObj);
        } else {
          setChannelRatings({});
        }
      } else {
        toast.error(response.message || "Failed to load promoter details");
        navigate("/promoters");
      }
    } catch (error) {
      console.error("Error loading promoter details:", error);
      toast.error("An error occurred while loading promoter details");
      navigate("/promoters");
    } finally {
      setIsLoading(false);
    }
  }, [affiliateCode, navigate]);

  useEffect(() => {
    loadPromoterDetails();
  }, [loadPromoterDetails]);

  const handleChannelRatingChange = (channelKey, criteriaId, rating) => {
    setChannelRatings((prev) => ({
      ...prev,
      [channelKey]: {
        ...(prev[channelKey] || {}),
        [criteriaId]: rating,
      },
    }));
  };

  const handleRiskCheckChange = (checkId, status) => {
    setRiskChecks((prev) => ({
      ...prev,
      [checkId]: status,
    }));
  };

  const handleScoringCalculated = (data) => {
    setScoringData(data);
  };

  const transformChannelRatings = () => {
    const ratingsArray = [];
    
    // Add channel ratings if they exist
    if (promoter?.promotion_channels && Object.keys(channelRatings).length > 0) {
      Object.entries(promoter.promotion_channels).forEach(([platform, channelArray]) => {
        if (Array.isArray(channelArray)) {
          channelArray.forEach((channel, index) => {
            const channelKey = `${platform}_${index}`;
            const ratings = channelRatings[channelKey];
            
            if (ratings && Object.keys(ratings).length > 0) {
              ratingsArray.push({
                platform,
                channel_index: index,
                channel_data: channel,
                ratings: Object.entries(ratings).map(([criteria, rating]) => ({
                  criteria,
                  rating,
                })),
              });
            }
          });
        }
      });
    }

    // Add publisher level metadata as a special entry in the array
    // This allows storing level info without adding a new column
    // Backend can store this in the channel_ratings array and we can retrieve it later
    ratingsArray.push({
      platform: "metadata",
      channel_index: -1,
      channel_data: {
        type: "publisher_level",
        level: levelAssignment.level,
        level_name: levelAssignment.level_name,
        assigned_at: new Date().toISOString(),
      },
      ratings: [],
    });

    return ratingsArray;
  };

  const transformRiskChecks = () => {
    if (!riskChecks || Object.keys(riskChecks).length === 0) {
      return [];
    }

    return Object.entries(riskChecks)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([checkId, passed]) => ({
        check_id: checkId,
        passed: Boolean(passed),
      }));
  };

  const handleApprove = async (approvalType, reason) => {
    setIsProcessing(true);
    try {
      const affiliateStatus = approvalType === "full" ? "full_approval" : "limited_approval";
      
      const payload = {
        affiliate_code: affiliateCode,
        affiliate_status: affiliateStatus,
        status_reason: reason || (approvalType === "full" ? "Full approval granted" : "Limited approval granted"),
        channel_ratings: transformChannelRatings(), // Level info included in channel_ratings array
        risk_checks: transformRiskChecks(),
        // Include scoring data
        scoring_model: scoringData,
      };

      const response = await affiliateService.updateAffiliateData(payload);

      if (response.status && response.statusCode === "000") {
        const message = approvalType === "full" 
          ? "Promoter approved with full access" 
          : "Promoter approved with limited access";
        toast.success(message);
        
        // Reload promoter data to reflect updated status
        await loadPromoterDetails();
        
        // Stay on the page - do not navigate away
      } else {
        toast.error(response.message || "Failed to approve promoter");
      }
    } catch (error) {
      console.error("Error approving promoter:", error);
      toast.error("An error occurred while approving promoter");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActivate = async () => {
    setIsProcessing(true);
    const wasActive = promoter?.is_active === 1 || promoter?.is_active === true;
    try {
      const response = await affiliateService.activateAffiliate(affiliateCode);

      if (response.status && response.statusCode === "000") {
        const action = wasActive ? "deactivated" : "activated";
        toast.success(`Publisher ${action} successfully`);
        
        // Reload promoter data to get updated is_active status
        await loadPromoterDetails();
        
        // Don't navigate away, let user see the updated status
        // setTimeout(() => {
        //   navigate("/promoters");
        // }, 1500);
      } else {
        const action = wasActive ? "deactivate" : "activate";
        toast.error(response.message || `Failed to ${action} publisher`);
      }
    } catch (error) {
      console.error("Error toggling publisher status:", error);
      toast.error("An error occurred while updating publisher status");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason) => {
    setIsProcessing(true);
    try {
      const payload = {
        affiliate_code: affiliateCode,
        affiliate_status: "rejected",
        status_reason: reason || "Application rejected",
        channel_ratings: transformChannelRatings(),
        risk_checks: transformRiskChecks(),
      };

      const response = await affiliateService.updateAffiliateData(payload);

      if (response.status && response.statusCode === "000") {
        toast.success("Promoter rejected successfully");
        
        setTimeout(() => {
          navigate("/promoters");
        }, 1500);
      } else {
        toast.error(response.message || "Failed to reject promoter");
      }
    } catch (error) {
      console.error("Error rejecting promoter:", error);
      toast.error("An error occurred while rejecting promoter");
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate validation status
  const getValidationStatus = () => {
    if (!promoter) return { basicValidationPassed: false };

    const profileCompletion = calculateProfileCompleteness(promoter);
    const channelCount = countPromotionChannels(promoter.promotion_channels);

    // At least one contact method verified (phone OR email)
    const contactVerified = !!promoter.phone_number_verified_at || !!promoter.email_verified_at;

    const basicValidationPassed =
      contactVerified &&
      channelCount > 0 &&
      profileCompletion.percentage >= 80;

    // Check channel ratings
    const allChannels = [];
    if (promoter.promotion_channels) {
      Object.entries(promoter.promotion_channels).forEach(([platform, channelArray]) => {
        if (Array.isArray(channelArray)) {
          channelArray.forEach((channel, index) => {
            allChannels.push(`${platform}_${index}`);
          });
        }
      });
    }

    const assessmentCriteria = ["audience_relevance", "engagement", "content_clarity", "posting_consistency"];
    const allChannelsRated = allChannels.every((channelKey) => {
      const ratings = channelRatings[channelKey] || {};
      return assessmentCriteria.every((criteria) => ratings[criteria]);
    });

    // Check risk assessment
    const riskCheckKeys = ["no_spam", "no_fake_followers", "no_misleading_content", "no_previous_abuse"];
    const allRiskChecked = riskCheckKeys.every((key) => riskChecks[key] !== null && riskChecks[key] !== undefined);
    const allRiskPassed = riskCheckKeys.every((key) => riskChecks[key] === true);
    const someRiskFailed = riskCheckKeys.some((key) => riskChecks[key] === false);

    return {
      basicValidationPassed,
      allChannelsRated,
      allRiskChecked,
      allRiskPassed,
      someRiskFailed,
      channelCount: allChannels.length,
    };
  };

  const validationStatus = getValidationStatus();

  // Can approve with full access if all checks pass
  const canApprove = 
    validationStatus.basicValidationPassed &&
    validationStatus.allChannelsRated &&
    validationStatus.allRiskChecked &&
    validationStatus.allRiskPassed;

  // Can approve with limited access if basic validation passes and channels are rated
  const canApproveLimited = 
    validationStatus.basicValidationPassed &&
    validationStatus.allChannelsRated &&
    validationStatus.allRiskChecked;

  // Calculate overall score for scoring model
  const calculateOverallScore = () => {
    const weights = {
      engagement_quality: 30,
      conversion_performance: 40,
      compliance: 20,
      consistency: 10,
    };

    // Engagement Quality (30%) - Based on channel ratings
    let engagementScore = 0;
    let engagementMax = 0;
    if (channelRatings && Object.keys(channelRatings).length > 0) {
      const scores = { high: 3, medium: 2, low: 1 };
      Object.values(channelRatings).forEach((ratings) => {
        if (ratings && typeof ratings === "object") {
          Object.values(ratings).forEach((rating) => {
            if (rating) {
              engagementScore += scores[rating] || 0;
              engagementMax += 3;
            }
          });
        }
      });
    }
    const engagementPercentage = engagementMax > 0 ? (engagementScore / engagementMax) * 100 : 0;
    const engagementWeighted = (engagementPercentage * weights.engagement_quality) / 100;

    // Conversion Performance (40%) - Not available at approval stage
    const conversionWeighted = 0;

    // Compliance (20%) - Based on risk checks
    let complianceScore = 0;
    let complianceMax = 0;
    if (riskChecks && Object.keys(riskChecks).length > 0) {
      Object.values(riskChecks).forEach((passed) => {
        complianceMax += 1;
        if (passed === true) {
          complianceScore += 1;
        }
      });
    }
    const compliancePercentage = complianceMax > 0 ? (complianceScore / complianceMax) * 100 : 0;
    const complianceWeighted = (compliancePercentage * weights.compliance) / 100;

    // Consistency (10%) - Based on profile completeness
    const profileCompletion = calculateProfileCompleteness(promoter);
    const consistencyPercentage = profileCompletion.percentage;
    const consistencyWeighted = (consistencyPercentage * weights.consistency) / 100;

    return engagementWeighted + conversionWeighted + complianceWeighted + consistencyWeighted;
  };

  const overallScore = calculateOverallScore();

  // Steps configuration
  const steps = [
    {
      id: "profile",
      badge: "Step 1",
      title: "Profile Details",
      description: "Review promoter info",
      enabled: true,
    },
    {
      id: "validation",
      badge: "Step 2",
      title: "Basic Validation",
      description: "Automated checks",
      enabled: true,
    },
    {
      id: "channels",
      badge: "Step 3",
      title: "Channel Quality",
      description: "Review & rate channels",
      enabled: validationStatus.basicValidationPassed,
    },
    {
      id: "risk",
      badge: "Step 4",
      title: "Risk & Compliance",
      description: "Verify compliance",
      enabled: validationStatus.basicValidationPassed,
    },
    {
      id: "scoring",
      badge: "Step 5",
      title: "Scoring Model",
      description: "Publisher scoring",
      enabled: validationStatus.allChannelsRated && validationStatus.allRiskChecked,
    },
    {
      id: "level",
      badge: "Step 6",
      title: "Level Assignment",
      description: "Publisher level",
      enabled: validationStatus.allChannelsRated && validationStatus.allRiskChecked,
    },
    {
      id: "activation",
      badge: "Step 7",
      title: "Activation Status",
      description: "Activation requirements",
      enabled: true,
    },
  ];

  // Calculate step status
  const getStepStatus = () => {
    const status = {};
    
    // Validation step
    if (validationStatus.basicValidationPassed) {
      status.validation = "completed";
    } else {
      status.validation = "failed";
    }

    // Channels step
    if (validationStatus.allChannelsRated) {
      status.channels = "completed";
    } else if (validationStatus.channelCount > 0) {
      status.channels = "warning";
    } else {
      status.channels = "pending";
    }

    // Risk step
    if (validationStatus.allRiskChecked) {
      if (validationStatus.allRiskPassed) {
        status.risk = "completed";
      } else if (validationStatus.someRiskFailed) {
        status.risk = "warning";
      }
    } else {
      status.risk = "pending";
    }

    // Profile step is always available
    status.profile = "completed";

    // Scoring step - available after channels and risk are completed
    if (validationStatus.allChannelsRated && validationStatus.allRiskChecked) {
      status.scoring = "completed";
    } else {
      status.scoring = "pending";
    }

    // Level step - available after scoring
    if (validationStatus.allChannelsRated && validationStatus.allRiskChecked) {
      status.level = "completed";
    } else {
      status.level = "pending";
    }

    // Activation step is always available (informational)
    status.activation = "completed";

    return status;
  };

  const stepStatus = getStepStatus();

  if (isLoading) {
    return (
      <>
        <Navbar page="Promoter Approval" />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#FF6C6C] animate-spin" />
            <p className="text-gray-600">Loading promoter details...</p>
          </div>
        </div>
      </>
    );
  }

  if (!promoter) {
    return (
      <>
        <Navbar page="Promoter Approval" />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <p className="text-gray-600">Promoter not found</p>
            <button
              onClick={() => navigate("/promoters")}
              className="mt-4 px-4 py-2 text-white bg-[#FF6C6C] rounded-lg hover:bg-[#ff5a5a]"
            >
              Back to Promoters
            </button>
          </div>
        </div>
      </>
    );
  }

  // Render content based on current step
  const renderStepContent = () => {
    const variants = {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
    };

    switch (currentStep) {
      case "profile":
        return (
          <motion.div
            key="profile"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <ContactInfoSection promoter={promoter} />
            <PromoterProfileDetails promoter={promoter} />
          </motion.div>
        );

      case "validation":
        return (
          <motion.div
            key="validation"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <BasicValidationChecks promoter={promoter} />
          </motion.div>
        );

      case "channels":
        return (
          <motion.div
            key="channels"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <ChannelQualityAssessment
              promoter={promoter}
              channelRatings={channelRatings}
              onChannelRatingChange={handleChannelRatingChange}
            />
          </motion.div>
        );

      case "risk":
        return (
          <motion.div
            key="risk"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <RiskComplianceReview
              riskChecks={riskChecks}
              onRiskCheckChange={handleRiskCheckChange}
            />
          </motion.div>
        );

      case "scoring":
        return (
          <motion.div
            key="scoring"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <PublisherScoringModel
              promoter={{
                ...promoter,
                profile_completeness: calculateProfileCompleteness(promoter).percentage,
              }}
              channelRatings={channelRatings}
              riskChecks={riskChecks}
              onScoringCalculated={handleScoringCalculated}
            />
          </motion.div>
        );

      case "level":
        return (
          <motion.div
            key="level"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <PublisherLevelAssignment
              promoter={promoter}
              overallScore={overallScore}
            />
          </motion.div>
        );

      case "activation":
        return (
          <motion.div
            key="activation"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <ActivationRequirements promoter={promoter} />
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        .badge-container::-webkit-scrollbar {
          display: none;
        }
        .badge-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .badge-container > * {
          flex-shrink: 0 !important;
        }
      `}</style>
      <Navbar page="Promoter Approval" />
      <div className="pb-32 min-h-screen bg-gray-50">
        <div className="px-4 py-6 mx-auto max-w-7xl md:px-6">
          {/* Header */}
          <div className="flex overflow-hidden relative gap-2 sm:gap-3 items-center p-3 sm:p-4 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm md:gap-4 md:p-6">
            {/* Subtle gradient accent */}
            <div className="absolute top-0 left-0 w-1 h-full" style={{ background: 'linear-gradient(to bottom, #FF6C6C, #ff8a8a)' }}></div>
            
            <button
              onClick={() => navigate("/promoters")}
              className="flex-shrink-0 p-1.5 sm:p-2 text-gray-600 rounded-lg transition-all hover:bg-gray-100 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            <div className="flex-1 pl-1 sm:pl-2 min-w-0">
              <div className="flex flex-wrap gap-2 items-baseline mb-1.5 sm:mb-2">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: '#FF6C6C' }}>
                  {promoter?.first_name} {promoter?.last_name}
                </h1>
                {promoter?.account_display_name && (
                  <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium text-gray-600 bg-gray-100 rounded-md whitespace-nowrap">
                    @{promoter.account_display_name}
                  </span>
                )}
              </div>
              
              <div className="badge-container flex flex-nowrap gap-1 sm:gap-1.5 md:gap-3 items-center overflow-x-auto w-full">
                <div className="flex-shrink-0 flex gap-0.5 sm:gap-1 items-center px-1 sm:px-1.5 md:px-2.5 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-medium text-gray-700 bg-gray-50 rounded border border-gray-200">
                  <Hash className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-gray-500" />
                  <span className="font-mono whitespace-nowrap">{promoter?.affiliate_code}</span>
                </div>
                
                {promoter?.affiliate_status && (
                  <span className={`flex-shrink-0 inline-flex px-1 sm:px-1.5 md:px-2.5 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-semibold rounded whitespace-nowrap ${
                    promoter.affiliate_status === "full_approval" ? "text-green-700 bg-green-100" :
                    promoter.affiliate_status === "limited_approval" ? "text-blue-700 bg-blue-100" :
                    promoter.affiliate_status === "pending" ? "text-yellow-700 bg-yellow-100" :
                    promoter.affiliate_status === "rejected" ? "text-red-700 bg-red-100" :
                    "text-gray-700 bg-gray-100"
                  }`}>
                    {promoter.affiliate_status === "full_approval" ? "Full" :
                     promoter.affiliate_status === "limited_approval" ? "Limited" :
                     promoter.affiliate_status === "pending" ? "Pending" :
                     promoter.affiliate_status === "rejected" ? "Rejected" :
                     promoter.affiliate_status}
                  </span>
                )}
                
                <div className="flex-shrink-0 flex gap-0.5 sm:gap-1 items-center text-[9px] sm:text-[10px] md:text-xs lg:text-sm text-gray-600">
                  <Mail className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate max-w-[80px] sm:max-w-[100px] md:max-w-[150px] lg:max-w-none whitespace-nowrap">{promoter?.email}</span>
                  {promoter?.email_verified_at ? (
                    <CheckCircle2 className="flex-shrink-0 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-green-600" title="Email verified" />
                  ) : (
                    <XCircle className="flex-shrink-0 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-gray-400" title="Email not verified" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Layout: Stepper + Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            {/* Left: Stepper Navigation */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <StepperNavigation
                  currentStep={currentStep}
                  steps={steps}
                  onStepChange={setCurrentStep}
                  stepStatus={stepStatus}
                />
              </div>
            </div>

            {/* Right: Main Content */}
            <div className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <AnimatePresence mode="wait">
                  {renderStepContent()}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <ActionButtons
          promoter={promoter}
          validationStatus={validationStatus}
          onApprove={handleApprove}
          onReject={handleReject}
          onActivate={handleActivate}
          isLoading={isProcessing}
          canApprove={canApprove}
          canApproveLimited={canApproveLimited}
        />
      </div>
    </>
  );
};

export default PromoterApproval;

